import dayjs from 'dayjs';
import { localizedName, displayValue, rangeLabel } from '../../utils/labFlags';

// The printable lab result sheet: one A4 page (more if the visit has many
// tests) with the clinic header, the patient, and every test on the visit as a
// table of value / unit / range / flag.
//
// Opens a print window with the sheet as static HTML, the way the sick-leave
// and medical-report prints do, rather than hiding a portal in the page: the
// visit page already has a hidden prescription sheet for window.print(), and
// two of them would print on top of each other.
//
//   rows       VisitLabTest documents of the visit
//   fieldById  live LabTestField rows, for translated labels
//   sex        patient's sex, for the range that applies
//   lang       'en' | 'ar' | 'ku'
//   t          the translator
// Returns false when the browser blocked the pop-up.

const escapeHtml = (value) => String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

const FLAG_LETTER = { low: 'L', high: 'H', abnormal: '!', normal: '' };

const storedRange = (result, liveField, sex) => {
    if (liveField) return rangeLabel(liveField, sex);
    const snap = result.snapshot || {};
    return rangeLabel({
        dataType: snap.dataType,
        referenceRangeLow: snap.referenceRangeLow,
        referenceRangeHigh: snap.referenceRangeHigh,
        referenceText: snap.referenceText
    });
};

export const buildLabSheetHtml = ({ visit, patient, doctorInfo = {}, rows = [], fieldById = new Map(), sex, lang = 'en', t }) => {
    const rtl = lang === 'ar' || lang === 'ku';
    const tr = (key, fallback) => {
        const value = t ? t(key) : null;
        return value && value !== key ? value : fallback;
    };

    const age = patient?.dateOfBirth
        ? dayjs().diff(dayjs(patient.dateOfBirth), 'year')
        : (patient?.age || '-');

    const testBlocks = rows.map((row) => {
        const live = row.labTestId && typeof row.labTestId === 'object' ? row.labTestId : null;
        const title = live ? localizedName(live, lang) : (row.testSnapshot?.name || '');
        const lines = (row.results || []).map((result) => {
            const fieldId = String(result.labTestFieldId?._id || result.labTestFieldId);
            const liveField = fieldById.get(fieldId);
            const label = liveField ? localizedName(liveField, lang) : (result.snapshot?.label || '');
            const value = displayValue(result.value, result.snapshot?.dataType, (key, fallback) => tr(key, fallback));
            const flag = result.flag || '';
            return `
                <tr class="${flag ? `flag-${flag}` : ''}">
                    <td class="label">${escapeHtml(label)}</td>
                    <td class="value">${escapeHtml(value)}</td>
                    <td class="unit">${escapeHtml(result.snapshot?.unit || liveField?.unit || '')}</td>
                    <td class="range">${escapeHtml(storedRange(result, liveField, sex))}</td>
                    <td class="flag">${FLAG_LETTER[flag] || ''}</td>
                </tr>`;
        }).join('');

        return `
            <section class="test">
                <h2>${escapeHtml(title)}</h2>
                <table>
                    <thead>
                        <tr>
                            <th>${escapeHtml(tr('labTest', 'Test'))}</th>
                            <th>${escapeHtml(tr('result', 'Result'))}</th>
                            <th>${escapeHtml(tr('labUnit', 'Unit'))}</th>
                            <th>${escapeHtml(tr('referenceRange', 'Reference range'))}</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>${lines}</tbody>
                </table>
                ${row.note ? `<p class="note">${escapeHtml(row.note)}</p>` : ''}
            </section>`;
    }).join('');

    return `<!DOCTYPE html>
<html lang="${lang}" dir="${rtl ? 'rtl' : 'ltr'}">
<head>
<meta charset="utf-8">
<title>${escapeHtml(tr('labResultSheet', 'Laboratory Result Sheet'))} - ${escapeHtml(patient?.name || '')}</title>
<style>
    @page { size: A4 portrait; margin: 14mm 12mm; }
    * { box-sizing: border-box; }
    body {
        font-family: 'Segoe UI', Tahoma, Arial, sans-serif;
        color: #1f2a37;
        margin: 0;
        font-size: 11pt;
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
    }
    .sheet { width: 186mm; margin: 0 auto; }
    header {
        display: flex; justify-content: space-between; align-items: flex-start;
        border-bottom: 2px solid #05668D; padding-bottom: 4mm; margin-bottom: 4mm;
    }
    .clinic h1 { font-size: 16pt; margin: 0 0 1mm; color: #05668D; }
    .clinic div { font-size: 9.5pt; color: #4a5a68; }
    .doc { text-align: end; font-size: 9.5pt; color: #4a5a68; }
    .doc strong { display: block; font-size: 11pt; color: #1f2a37; }
    .title { text-align: center; font-size: 13pt; font-weight: 600; letter-spacing: 0.08em; text-transform: uppercase; margin: 0 0 3mm; color: #05668D; }
    .patient {
        display: grid; grid-template-columns: repeat(4, 1fr); gap: 2mm 6mm;
        background: #f3f7fa; border-radius: 2mm; padding: 3mm 4mm; margin-bottom: 4mm; font-size: 10pt;
    }
    .patient span { color: #6b7a87; font-size: 8.5pt; display: block; }
    .test { margin-bottom: 4mm; page-break-inside: avoid; }
    .test h2 { font-size: 11.5pt; margin: 0 0 1.5mm; padding: 1.5mm 3mm; background: #e9f1f6; border-inline-start: 3px solid #05668D; }
    table { width: 100%; border-collapse: collapse; font-size: 10pt; }
    th, td { padding: 1.4mm 3mm; text-align: start; border-bottom: 1px solid #e3e9ee; }
    th { font-size: 8.5pt; text-transform: uppercase; letter-spacing: 0.04em; color: #6b7a87; font-weight: 600; }
    td.value { font-weight: 600; font-variant-numeric: tabular-nums; white-space: nowrap; }
    td.unit, td.range { color: #4a5a68; white-space: nowrap; }
    td.flag { font-weight: 700; width: 8mm; text-align: center; }
    tr.flag-high td.value, tr.flag-high td.flag, tr.flag-abnormal td.value, tr.flag-abnormal td.flag { color: #c62828; }
    tr.flag-low td.value, tr.flag-low td.flag { color: #1565c0; }
    .note { font-size: 9.5pt; color: #4a5a68; margin: 1.5mm 3mm 0; white-space: pre-wrap; }
    footer { margin-top: 8mm; display: flex; justify-content: space-between; font-size: 9pt; color: #6b7a87; border-top: 1px solid #e3e9ee; padding-top: 2mm; }
    .legend span { margin-inline-end: 4mm; }
    .sign { margin-top: 12mm; text-align: end; font-size: 10pt; }
    .sign div { display: inline-block; border-top: 1px solid #1f2a37; padding-top: 1.5mm; min-width: 50mm; text-align: center; }
    .empty { color: #6b7a87; padding: 6mm 0; text-align: center; }
</style>
</head>
<body>
<div class="sheet">
    <header>
        <div class="clinic">
            <h1>${escapeHtml(doctorInfo.clinicName || doctorInfo.name || '')}</h1>
            ${doctorInfo.clinicAddress ? `<div>${escapeHtml(doctorInfo.clinicAddress)}</div>` : ''}
            ${doctorInfo.clinicPhone ? `<div>${escapeHtml(doctorInfo.clinicPhone)}</div>` : ''}
        </div>
        <div class="doc">
            <strong>${escapeHtml(doctorInfo.name || '')}</strong>
            ${doctorInfo.specialization ? `<div>${escapeHtml(doctorInfo.specialization)}</div>` : ''}
        </div>
    </header>

    <div class="title">${escapeHtml(tr('labResultSheet', 'Laboratory Result Sheet'))}</div>

    <div class="patient">
        <div><span>${escapeHtml(tr('patientName', 'Patient'))}</span>${escapeHtml(patient?.name || '-')}</div>
        <div><span>${escapeHtml(tr('age', 'Age'))} / ${escapeHtml(tr('gender', 'Sex'))}</span>${escapeHtml(age)} / ${escapeHtml(patient?.gender ? tr(patient.gender, patient.gender) : '-')}</div>
        <div><span>${escapeHtml(tr('visitDate', 'Visit date'))}</span>${visit?.visitDate ? dayjs(visit.visitDate).format('YYYY-MM-DD') : '-'}</div>
        <div><span>${escapeHtml(tr('phoneNumber', 'Phone'))}</span>${escapeHtml(patient?.phoneNumber || '-')}</div>
    </div>

    ${testBlocks || `<div class="empty">${escapeHtml(tr('noLabResults', 'No lab tests on this visit'))}</div>`}

    <div class="sign"><div>${escapeHtml(tr('doctorSignature', 'Signature'))}</div></div>

    <footer>
        <div class="legend">
            <span><b>L</b> ${escapeHtml(tr('labFlagLow', 'Low'))}</span>
            <span><b>H</b> ${escapeHtml(tr('labFlagHigh', 'High'))}</span>
            <span><b>!</b> ${escapeHtml(tr('labFlagAbnormal', 'Abnormal'))}</span>
        </div>
        <div>${escapeHtml(tr('printedOn', 'Printed'))} ${dayjs().format('YYYY-MM-DD HH:mm')}</div>
    </footer>
</div>
</body>
</html>`;
};

export const printLabSheet = (args) => {
    const html = buildLabSheetHtml(args);
    const win = window.open('', '_blank', 'width=900,height=1000');
    if (!win) return false;
    win.document.open();
    win.document.write(html);
    win.document.close();
    // Give the window a moment to lay out before the print dialog measures it.
    win.focus();
    setTimeout(() => {
        try {
            win.print();
        } catch (error) {
            console.error('Print failed:', error);
        }
    }, 250);
    return true;
};

export default printLabSheet;
